import { useState, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { FrogAvatar, getStageDaysRequired } from "@/components/pond/FrogAvatar";
import { Progress } from "@/components/ui/progress";
import { Check, Trash2, Moon, Loader2, Camera, X, Pencil, History } from "lucide-react";
import { Label } from "@/components/ui/label";
import { HabitHistoryModal } from "./HabitHistoryModal";
import { EditHabitDialog, EditHabitData } from "./EditHabitDialog";
import { HabitFormData } from "./AddHabitDialog";

interface HabitDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  habit: {
    id: string;
    name: string;
    frequencyType: string;
    frequencyCount: number;
    evidenceType: string | null;
    isPrivate?: boolean;
    locationLat: number | null;
    locationLng: number | null;
    locationRadius?: number | null;
    scheduledTime?: string | null;
    timezone?: string | null;
    frog: {
      stage: "egg" | "tadpole" | "young" | "adult" | "hibernating";
      daysCompleted: number;
      currentStreak: number;
    };
  } | null;
  onComplete: (habitId: string, evidenceFile?: File) => Promise<void>;
  onHibernate: (habitId: string) => Promise<void>;
  onDelete: (habitId: string) => Promise<void>;
  onUpdate: (habitId: string, data: HabitFormData) => Promise<void>;
}

const stageProgress = {
  egg: { min: 0, max: 3, next: "Girino" },
  tadpole: { min: 3, max: 10, next: "Jovem" },
  young: { min: 10, max: 31, next: "Adulto" },
  adult: { min: 31, max: 31, next: null },
  hibernating: { min: 0, max: 0, next: null },
};

const frequencyLabels: Record<string, string> = {
  daily: "diário",
  weekly: "semanal",
  monthly: "mensal",
  yearly: "anual",
};

export const HabitDetailSheet = ({
  open,
  onOpenChange,
  habit,
  onComplete,
  onHibernate,
  onDelete,
  onUpdate,
}: HabitDetailSheetProps) => {
  const [isCompleting, setIsCompleting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  if (!habit) return null;

  const requiresPhoto = habit.evidenceType === "photo";
  const { frog } = habit;
  const progress = stageProgress[frog.stage];
  const progressPercent = frog.stage === "adult" 
    ? 100 
    : ((frog.daysCompleted - progress.min) / (progress.max - progress.min)) * 100;

  const daysUntilNext = Math.max(0, progress.max - frog.daysCompleted);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const url = URL.createObjectURL(file);
      setImagePreview(url);
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleComplete = async () => {
    if (requiresPhoto && !selectedImage) return;
    setIsCompleting(true);
    try {
      await onComplete(habit.id, selectedImage || undefined);
      clearImage();
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-card border-border overflow-y-auto p-6 sm:p-6">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-2xl font-display text-center pr-8">
            {habit.name}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6 pb-6">{/* Frog display */}
          {/* Frog display */}
          <div className="flex flex-col items-center">
            <FrogAvatar 
              stage={frog.stage} 
              size="xl" 
              daysCompleted={frog.daysCompleted}
            />
            <p className="mt-4 text-lg font-semibold capitalize">
              {frog.stage === "egg" && "Ovo"}
              {frog.stage === "tadpole" && "Girino"}
              {frog.stage === "young" && "Sapo Jovem"}
              {frog.stage === "adult" && "Sapo Adulto"}
              {frog.stage === "hibernating" && "Hibernando"}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="pond-card text-center">
              <p className="text-3xl font-bold text-coral">{frog.daysCompleted}</p>
              <p className="text-sm text-muted-foreground">dias completos</p>
            </div>
            <div className="pond-card text-center">
              <p className="text-3xl font-bold text-golden">
                {frog.currentStreak} 🔥
              </p>
              <p className="text-sm text-muted-foreground">sequência atual</p>
            </div>
          </div>

          {/* Progress to next stage */}
          {frog.stage !== "adult" && frog.stage !== "hibernating" && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Próximo estágio</span>
                <span className="font-semibold">{progress.next}</span>
              </div>
              <Progress value={progressPercent} className="h-3" />
              <p className="text-xs text-center text-muted-foreground">
                {daysUntilNext} dias restantes
              </p>
            </div>
          )}

          {/* Frequency info */}
          <div className="text-center text-sm text-muted-foreground">
            <p>
              {habit.frequencyCount}x {frequencyLabels[habit.frequencyType]}
            </p>
          </div>

          {/* Image upload for photo evidence */}
          {requiresPhoto && frog.stage !== "hibernating" && (
            <div className="space-y-3">
              <Label className="font-semibold text-sm">📷 Foto de evidência</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageSelect}
                className="hidden"
              />
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Evidência"
                    className="w-full h-48 object-cover rounded-xl border border-border"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-8 w-8 rounded-full"
                    onClick={clearImage}
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full h-24 border-dashed border-2 flex flex-col gap-2"
                  onClick={() => fileInputRef.current?.click()}
                  type="button"
                >
                  <Camera className="h-6 w-6 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Tirar foto ou escolher imagem</span>
                </Button>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-4 pt-2">
            <Button
              variant="coral"
              size="lg"
              className="w-full h-12"
              onClick={handleComplete}
              disabled={frog.stage === "hibernating" || isCompleting || (requiresPhoto && !selectedImage)}
            >
              {isCompleting ? (
                <>
                  <Loader2 className="mr-2 animate-spin" />
                  Marcando...
                </>
              ) : (
                <>
                  <Check className="mr-2" />
                  Marcar como feito
                </>
              )}
            </Button>

            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="secondary"
                className="h-11"
                onClick={() => onHibernate(habit.id)}
                disabled={frog.stage === "hibernating"}
              >
                <Moon className="mr-2 h-4 w-4" />
                Hibernar
              </Button>
              <Button
                variant="outline"
                className="h-11 text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => onDelete(habit.id)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir
              </Button>
            </div>

            {/* Edit button */}
            <Button
              variant="outline"
              className="w-full h-11"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Editar hábito
            </Button>

            {/* History button */}
            <Button
              variant="outline"
              className="w-full h-11 text-emerald-700 border-emerald-700 hover:bg-emerald-700/10 dark:text-emerald-400 dark:border-emerald-400 dark:hover:bg-emerald-400/10"
              onClick={() => setHistoryOpen(true)}
            >
              <History className="mr-2 h-4 w-4" />
              Histórico
            </Button>
          </div>
        </div>
      </SheetContent>

      {/* History Modal */}
      {habit && (
        <HabitHistoryModal
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          habitId={habit.id}
          habitName={habit.name}
          evidenceType={habit.evidenceType}
          locationLat={habit.locationLat}
          locationLng={habit.locationLng}
        />
      )}

      {/* Edit Habit Dialog */}
      {habit && (
        <EditHabitDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          habit={{
            id: habit.id,
            name: habit.name,
            frequencyType: habit.frequencyType,
            frequencyCount: habit.frequencyCount,
            evidenceType: habit.evidenceType,
            isPrivate: habit.isPrivate ?? true,
            locationLat: habit.locationLat,
            locationLng: habit.locationLng,
            locationRadius: habit.locationRadius ?? null,
            scheduledTime: habit.scheduledTime ?? null,
            timezone: habit.timezone ?? null,
          }}
          onSubmit={onUpdate}
        />
      )}
    </Sheet>
  );
};
